import { API_BASE, authHeaders } from './apiAuth'

/**
 * The Vectorize card's paid way: Recraft through fal (Worker:
 * routes/vectorize.ts). The board posts a copy of the picture, then asks
 * until the SVG is kept in the project. $0.04 a picture, 40 credits.
 */
export const FAL_VECTOR_CREDITS = 40

async function reason(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string }
    if (body?.error) return body.error
  } catch {
    /* not JSON */
  }
  return `HTTP ${response.status}`
}

/** A copy Recraft takes: PNG (JPEG if too big), 257-2,048 pixels a side, under 5 MB. */
async function copyForRecraft(source: HTMLCanvasElement): Promise<Blob> {
  const long = Math.max(source.width, source.height, 1)
  const short = Math.max(1, Math.min(source.width, source.height))
  let scale = Math.min(1, 2048 / long)
  if (short * scale < 300) scale = Math.min(4000 / long, 300 / short)
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(source.width * scale))
  canvas.height = Math.max(1, Math.round(source.height * scale))
  const context = canvas.getContext('2d')
  if (!context) throw new Error('This browser could not read the picture')
  context.imageSmoothingQuality = 'high'
  context.drawImage(source, 0, 0, canvas.width, canvas.height)
  const as = (type: string, quality?: number) =>
    new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality))
  const png = await as('image/png')
  if (png && png.size < 4.8 * 1024 * 1024) return png
  const jpeg = await as('image/jpeg', 0.9)
  if (!jpeg) throw new Error('The picture could not be prepared')
  return jpeg
}

/** Vectorize with Recraft; answers the kept SVG's project file id. */
export async function vectorizeWithFal(ask: {
  projectId: string
  source: HTMLCanvasElement
  from: string
  title: string
}): Promise<string> {
  const copy = await copyForRecraft(ask.source)
  const project = encodeURIComponent(ask.projectId)
  const started = await fetch(`${API_BASE}/api/canvas-vectorize?project=${project}`, {
    method: 'POST',
    headers: { 'Content-Type': copy.type, ...(await authHeaders()) },
    body: copy,
  })
  if (!started.ok) throw new Error(await reason(started))
  const { task } = (await started.json()) as { task?: string }
  if (!task) throw new Error('fal did not take it')

  const startedAt = Date.now()
  let misses = 0
  for (;;) {
    await new Promise((wake) => setTimeout(wake, 2000))
    if (Date.now() - startedAt > 4 * 60 * 1000) throw new Error('No answer from fal after four minutes')
    try {
      const asked = await fetch(
        `${API_BASE}/api/canvas-vectorize?project=${project}&task=${encodeURIComponent(task)}` +
          `&from=${encodeURIComponent(ask.from)}&title=${encodeURIComponent(ask.title)}`,
        { headers: await authHeaders() },
      )
      if (!asked.ok) throw new Error(await reason(asked))
      const body = (await asked.json()) as { state?: string; fileId?: string; error?: string }
      misses = 0
      if (body.state === 'done' && body.fileId) return body.fileId
      if (body.state === 'failed') throw Object.assign(new Error(body.error || 'It could not be vectorized'), { final: true })
    } catch (error) {
      if ((error as { final?: boolean }).final) throw error
      misses += 1
      if (misses >= 5) throw error
    }
  }
}
